// ============================================================
// FLATTEN — Convert tree to flat array preserving depth
// ============================================================
export function flattenTree(nodes) {
    const result = [];
    for (const node of nodes) {
        result.push(node);
        if (node.children.length > 0) {
            result.push(...flattenTree(node.children));
        }
    }
    return result;
}
// ============================================================
// FIND — Locate nodes in tree
// ============================================================
export function findNodeById(nodes, id) {
    for (const node of nodes) {
        if (node.id === id)
            return node;
        const found = findNodeById(node.children, id);
        if (found)
            return found;
    }
    return null;
}
export function findNodeByRowId(nodes, rowId) {
    for (const node of nodes) {
        if (node.rowId === rowId)
            return node;
        const found = findNodeByRowId(node.children, rowId);
        if (found)
            return found;
    }
    return null;
}
export function findParent(nodes, id) {
    for (const node of nodes) {
        if (node.children.some((c) => c.id === id))
            return node;
        const found = findParent(node.children, id);
        if (found)
            return found;
    }
    return null;
}
/**
 * Find the sibling list that contains a node, plus the index within that list.
 */
export function findSiblingContext(nodes, id) {
    for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].id === id) {
            return { siblings: nodes, index: i, parent: null };
        }
    }
    // Search recursively
    for (const node of nodes) {
        for (let i = 0; i < node.children.length; i++) {
            if (node.children[i].id === id) {
                return { siblings: node.children, index: i, parent: node };
            }
        }
        const found = findSiblingContext(node.children, id);
        if (found)
            return found;
    }
    return null;
}
// ============================================================
// COLLECT — Gather all rowIds
// ============================================================
export function collectRowIds(nodes) {
    const ids = [];
    for (const node of nodes) {
        ids.push(node.rowId);
        ids.push(...collectRowIds(node.children));
    }
    return ids;
}
// ============================================================
// INSERT — Add a node to the tree
// ============================================================
/**
 * Insert a new node after the node with `afterId`.
 * If `afterId` is null, appends at the end of root.
 * If `afterId` is '__prepend__', prepends at the beginning of root.
 * If `asChild` is true, appends as last child of the `afterId` node.
 */
export function insertNode(nodes, newNode, afterId, asChild = false) {
    if (afterId === null) {
        return [...nodes, { ...newNode, depth: 0 }];
    }
    if (afterId === '__prepend__') {
        return [{ ...newNode, depth: 0 }, ...nodes];
    }
    if (asChild) {
        return nodes.map((node) => {
            if (node.id === afterId) {
                const childNode = { ...newNode, depth: node.depth + 1 };
                return { ...node, children: [...node.children, childNode] };
            }
            return { ...node, children: insertNode(node.children, newNode, afterId, true) };
        });
    }
    // Insert as sibling after afterId
    const result = [];
    for (const node of nodes) {
        if (node.id === afterId) {
            result.push(node);
            result.push({ ...newNode, depth: node.depth });
        }
        else {
            result.push({ ...node, children: insertNode(node.children, newNode, afterId, false) });
        }
    }
    return result;
}
// ============================================================
// REMOVE — Delete a node (and its children)
// ============================================================
export function removeNode(nodes, id) {
    return nodes
        .filter((node) => node.id !== id)
        .map((node) => ({
        ...node,
        children: removeNode(node.children, id),
    }));
}
// ============================================================
// UPDATE — Immutable update of node data
// ============================================================
export function updateNodeData(nodes, id, updates) {
    return nodes.map((node) => {
        if (node.id === id) {
            return { ...node, data: { ...node.data, ...updates } };
        }
        return { ...node, children: updateNodeData(node.children, id, updates) };
    });
}
export function updateNodeRowId(nodes, id, newRowId) {
    return nodes.map((node) => {
        if (node.id === id) {
            return { ...node, rowId: newRowId };
        }
        return { ...node, children: updateNodeRowId(node.children, id, newRowId) };
    });
}
// ============================================================
// MOVE — Reorder a node in the flattened representation
// ============================================================
/**
 * Move a node from its current position to after the `toId` node.
 * The moved node becomes a sibling at the same depth as `toId`.
 */
export function moveNode(nodes, fromId, toId) {
    // Extract the node
    const node = findNodeById(nodes, fromId);
    if (!node)
        return nodes;
    // Remove from current position
    let result = removeNode(nodes, fromId);
    // Find target to determine new depth
    const target = findNodeById(result, toId);
    if (!target)
        return nodes;
    // Insert after toId as sibling
    const movedNode = recalcDepths([{ ...node, depth: target.depth }], target.depth)[0];
    result = insertNode(result, movedNode, toId, false);
    return result;
}
// ============================================================
// INDENT / OUTDENT — Change nesting depth
// ============================================================
/**
 * Indent: make this node a child of its previous sibling.
 * The node (with its children) moves into the previous sibling's children list.
 */
export function indentNode(nodes, id) {
    const ctx = findSiblingContext(nodes, id);
    if (!ctx || ctx.index === 0)
        return nodes; // Can't indent first sibling
    // Deep clone to avoid mutation
    const cloned = deepCloneNodes(nodes);
    const clonedCtx = findSiblingContext(cloned, id);
    if (!clonedCtx)
        return nodes;
    const [nodeToMove] = clonedCtx.siblings.splice(clonedCtx.index, 1);
    if (!nodeToMove)
        return nodes;
    const prevSibling = clonedCtx.siblings[clonedCtx.index - 1];
    if (!prevSibling)
        return nodes;
    const reDepth = recalcDepths([nodeToMove], prevSibling.depth + 1)[0];
    prevSibling.children.push(reDepth);
    return cloned;
}
/**
 * Outdent: move this node to its parent's level, positioned after the parent.
 * Can only outdent if the node has a parent (is not at root level).
 */
export function outdentNode(nodes, id) {
    const parent = findParent(nodes, id);
    if (!parent)
        return nodes; // Already at root
    // Deep clone
    const cloned = deepCloneNodes(nodes);
    const clonedParent = findNodeById(cloned, parent.id);
    if (!clonedParent)
        return nodes;
    const childIndex = clonedParent.children.findIndex((c) => c.id === id);
    if (childIndex === -1)
        return nodes;
    // Remove from parent's children
    const [nodeToMove] = clonedParent.children.splice(childIndex, 1);
    if (!nodeToMove)
        return nodes;
    // Any siblings after the moved node become its children
    const trailingChildren = clonedParent.children.splice(childIndex);
    for (const trailing of trailingChildren) {
        nodeToMove.children.push(recalcDepths([trailing], nodeToMove.depth + 1)[0]);
    }
    // Recalc depth for the moved node
    const reDepth = recalcDepths([nodeToMove], clonedParent.depth)[0];
    // Insert after parent in the parent's sibling list
    const grandparentCtx = findSiblingContext(cloned, clonedParent.id);
    if (grandparentCtx) {
        grandparentCtx.siblings.splice(grandparentCtx.index + 1, 0, reDepth);
    }
    return cloned;
}
// ============================================================
// DEPTH RECALCULATION
// ============================================================
export function recalcDepths(nodes, startDepth = 0) {
    return nodes.map((node) => ({
        ...node,
        depth: startDepth,
        children: recalcDepths(node.children, startDepth + 1),
    }));
}
// ============================================================
// COLUMN HEADERS BINDING
// ============================================================
/**
 * Return all column_headers nodes from the tree.
 */
export function getColumnHeadersNodes(nodes) {
    return flattenTree(nodes).filter((n) => n.data.type === 'column_headers');
}
/**
 * Resolve the effective columnHeadersId for a data row node.
 * 1. If the node has an explicit columnHeadersId and it still exists, return it.
 * 2. If there is exactly 1 column_headers row, auto-default to it.
 * 3. Otherwise return undefined (will fall back to columnConfig.default).
 */
export function resolveColumnHeadersId(node, allNodes) {
    const headerNodes = getColumnHeadersNodes(allNodes);
    // Explicit binding — verify it still exists
    if (node.columnHeadersId) {
        const exists = headerNodes.some((h) => h.id === node.columnHeadersId);
        if (exists)
            return node.columnHeadersId;
    }
    // Auto-default when exactly one column_headers row
    if (headerNodes.length === 1) {
        return headerNodes[0].id;
    }
    return undefined;
}
/**
 * Immutable update: set columnHeadersId on a specific node.
 */
export function updateNodeColumnHeadersId(nodes, id, columnHeadersId) {
    return nodes.map((node) => {
        if (node.id === id) {
            return { ...node, columnHeadersId };
        }
        return { ...node, children: updateNodeColumnHeadersId(node.children, id, columnHeadersId) };
    });
}
/**
 * Clear all columnHeadersId references pointing to a deleted column_headers row.
 */
export function clearColumnHeadersBindings(nodes, deletedHeaderId) {
    return nodes.map((node) => {
        const updated = node.columnHeadersId === deletedHeaderId
            ? { ...node, columnHeadersId: undefined }
            : node;
        return {
            ...updated,
            children: clearColumnHeadersBindings(updated.children, deletedHeaderId),
        };
    });
}
// ============================================================
// HELPERS
// ============================================================
function deepCloneNodes(nodes) {
    return nodes.map((node) => ({
        ...node,
        data: { ...node.data },
        children: deepCloneNodes(node.children),
    }));
}
//# sourceMappingURL=tree-utils.js.map